/**
 * Source port of the Java CanvasDecoder.
 */
import { CanvasTokenType, CanvasTokenTypeObject } from '@fullstory/mobile-flatbuffers';
import { CanvasTokenDef, CanvasTokenDefs } from './canvastokendef';

class ByteBuffer {
  private buf: Uint8Array;
  private pos: number;
  private lim: number;

  constructor(buf: Uint8Array, offset: number, limit: number) {
    this.buf = buf;
    this.pos = offset;
    this.lim = limit;
  }

  public limit(): number {
    return this.lim;
  }

  public position(): number {
    return this.pos;
  }

  public get(i: number): number {
    return this.buf[i];
  }

  public getInt(i: number): number {
    return (
      this.buf[i + 0] | (this.buf[i + 1] << 8) | (this.buf[i + 2] << 16) | (this.buf[i + 3] << 24)
    );
  }
}

export interface CanvasDecoderCallback {
  op(op: number): void;
  value(token: CanvasTokenDef, i: any): void;
}

/**
 * Canvas entry decoder.
 */
export class CanvasDecoder {
  private static COLOR_ENCODING_PREVIOUS = 0;
  private static COLOR_ENCODING_BLACK = 1;
  private static COLOR_ENCODING_WHITE = 2;
  private static COLOR_ENCODING_OPAQUE_GRAYSCALE = 3;
  private static COLOR_ENCODING_OPAQUE_NON_GRAYSCALE = 4;
  private static COLOR_ENCODING_TRANSPARENT_BLACK = 5;
  private static COLOR_ENCODING_TRANSPARENT_GRAYSCALE = 6;
  private static COLOR_ENCODING_FULL = 7;

  private static VARFLOAT_SHORT_FLOAT = 0x7e;
  private static VARFLOAT_ZERO = 0x7f;
  private static VARFLOAT_ONE = 0xff;
  private static VARFLOAT_MINUS_ONE = 0xfe;
  private static FIXED_FLOAT_SCALE = 256;

  private buffer: ByteBuffer;
  private position: number;
  private limit: number;
  private firstToken: boolean;
  private token: CanvasTokenDef;
  private lastColor: number = 0x99000000;

  /**
   * Assumes a buffer with the position/limit set to bracket the canvas data. Because the buffer was written
   * backwards, limit is at the *start* of the canvas data.
   */
  constructor(
    private tokens: CanvasTokenDefs,
    private map: (token: CanvasTokenDef, value: number) => any,
    buffer: Uint8Array,
    offset: number,
    length: number,
  ) {
    this.reset(new ByteBuffer(buffer, offset, offset + length));
  }

  private reset(buffer: ByteBuffer) {
    this.firstToken = true;
    this.token = null;
    this.buffer = buffer;
    this.position = buffer.position();
    this.limit = buffer.limit();
    this.lastColor = 0x99000000;
  }

  public isComplete(): boolean {
    return this.position >= this.limit;
  }

  public read(callback: CanvasDecoderCallback): boolean {
    if (this.isComplete()) return false;

    const token = this.readToken();

    // For any numeric-type tokens, run them through map()
    switch (token.type) {
      case CanvasTokenType.OP:
        callback.op(this.readOp());
        break;
      case CanvasTokenType.VOID:
        callback.value(token, null);
        break;
      case CanvasTokenType.COLOR:
        callback.value(
          token,
          token.object == CanvasTokenTypeObject.NONE
            ? this.readColor()
            : this.map(this.token, this.readColor()),
        );
        break;
      case CanvasTokenType.FIXEDFLOAT:
        callback.value(
          token,
          token.object == CanvasTokenTypeObject.NONE
            ? this.readFixedFloat()
            : this.map(this.token, this.readFixedFloat()),
        );
        break;
      case CanvasTokenType.INT:
        callback.value(
          token,
          token.object == CanvasTokenTypeObject.NONE
            ? this.readInt()
            : this.map(this.token, this.readInt()),
        );
        break;
      case CanvasTokenType.MATRIX:
        callback.value(token, this.readMatrix());
        break;
      case CanvasTokenType.MATRIX6:
        callback.value(token, this.readMatrix6());
        break;
      case CanvasTokenType.POINT:
        callback.value(token, this.readPoint());
        break;
      case CanvasTokenType.POINTF:
        callback.value(token, this.readPointF());
        break;
      case CanvasTokenType.RECT:
        callback.value(token, this.readRect());
        break;
      case CanvasTokenType.ULONG:
        // JS doesn't have real 64-bit integers, so using them as indices is too much of a pain.
        if (token.object != CanvasTokenTypeObject.NONE) {
          throw new Error('ULONG token types may not be Objects!');
        }
        callback.value(token, this.readULong());
        break;
      case CanvasTokenType.VARFLOAT:
        callback.value(
          token,
          token.object == CanvasTokenTypeObject.NONE
            ? this.readVarFloat()
            : this.map(this.token, this.readVarFloat()),
        );
        break;
      case CanvasTokenType.VARINT:
        callback.value(
          token,
          token.object == CanvasTokenTypeObject.NONE
            ? this.readVarInt()
            : this.map(this.token, this.readVarInt()),
        );
        break;
      case CanvasTokenType.VARSINT:
        callback.value(
          token,
          token.object == CanvasTokenTypeObject.NONE
            ? this.readVarSInt()
            : this.map(this.token, this.readVarSInt()),
        );
        break;
      default:
        throw new Error(`Unhandled token type: ${token.type} (${token})`);
    }

    return true;
  }

  private readToken(): CanvasTokenDef {
    // First token is always OP
    if (this.firstToken) {
      this.firstToken = false;
      return (this.token = this.tokens.decode(0));
    }

    return (this.token = this.tokens.decode(this.decodeVarInt()));
  }

  public readOp() {
    this.token.expectType(CanvasTokenType.OP);
    return this.decodeVarInt();
  }

  public readMatrix() {
    this.token.expectType(CanvasTokenType.MATRIX);
    const matrix = [];
    for (let i = 0; i < 9; i++) matrix[i] = this.decodeVarFloat();
    return matrix;
  }

  public readMatrix6() {
    this.token.expectType(CanvasTokenType.MATRIX6);
    const matrix = [];
    for (let i = 0; i < 6; i++) matrix[i] = this.decodeVarFloat();
    return matrix;
  }

  public readColor() {
    this.token.expectType(CanvasTokenType.COLOR);
    const b = this.readByte() & 0xff;
    if (b == CanvasDecoder.COLOR_ENCODING_BLACK) return 0xff000000;
    if (b == CanvasDecoder.COLOR_ENCODING_WHITE) return 0xffffffff;
    if (b == CanvasDecoder.COLOR_ENCODING_PREVIOUS) return this.lastColor;

    // Update lastColor if it isn't one of our shortcuts
    return (this.lastColor = this.readEncodedColor(b));
  }

  private readEncodedColor(bArg: number) {
    let b = bArg;
    if (b == CanvasDecoder.COLOR_ENCODING_OPAQUE_GRAYSCALE) {
      b = this.readByte();
      return 0xff000000 | (b << 16) | (b << 8) | b;
    }
    if (b == CanvasDecoder.COLOR_ENCODING_OPAQUE_NON_GRAYSCALE) {
      return 0xff000000 | (this.readByte() << 16) | (this.readByte() << 8) | this.readByte();
    }

    if (b == CanvasDecoder.COLOR_ENCODING_TRANSPARENT_BLACK) {
      return this.readByte() << 24;
    }
    if (b == CanvasDecoder.COLOR_ENCODING_TRANSPARENT_GRAYSCALE) {
      const a = this.readByte();
      const c = this.readByte();
      return (a << 24) | (c << 16) | (c << 8) | c;
    }

    if (b == CanvasDecoder.COLOR_ENCODING_FULL) {
      return (
        (this.readByte() << 24) | (this.readByte() << 16) | (this.readByte() << 8) | this.readByte()
      );
    }

    return (b << 24) | (this.readByte() << 16) | (this.readByte() << 8) | this.readByte();
  }

  public readInt() {
    this.token.expectType(CanvasTokenType.INT);
    return this.decodeInt();
  }

  public readULong() {
    this.token.expectType(CanvasTokenType.ULONG);
    return this.decodeULong();
  }

  public readVarInt() {
    this.token.expectType(CanvasTokenType.VARINT);
    return this.decodeVarInt();
  }

  public readVarSInt() {
    this.token.expectType(CanvasTokenType.VARSINT);
    return this.decodeVarSInt();
  }

  public readRect() {
    this.token.expectType(CanvasTokenType.RECT);
    const x = this.decodeVarSInt();
    const y = this.decodeVarSInt();
    const w = this.decodeVarSInt();
    const h = this.decodeVarSInt();
    return [x, y, x + w, y + h];
  }

  public readPoint() {
    this.token.expectType(CanvasTokenType.POINT);
    return [this.decodeVarSInt(), this.decodeVarSInt()];
  }

  public readPointF() {
    this.token.expectType(CanvasTokenType.POINTF);
    return [this.decodeVarFloat(), this.decodeVarFloat()];
  }

  public readVarFloat() {
    this.token.expectType(CanvasTokenType.VARFLOAT);
    return this.decodeVarFloat();
  }

  public readFixedFloat() {
    this.token.expectType(CanvasTokenType.FIXEDFLOAT);
    return this.decodeVarSInt() / CanvasDecoder.FIXED_FLOAT_SCALE;
  }

  private decodeInt() {
    const value = this.buffer.getInt(this.position);
    this.position += 4;
    return value;
  }

  private decodeULong() {
    const lo = this.buffer.getInt(this.position);
    this.position += 4;
    const hi = this.buffer.getInt(this.position);
    this.position += 4;
    return (BigInt(hi) << BigInt(32)) | BigInt(lo);
  }

  private decodeVarInt() {
    let result = 0;
    let shift = 0;

    for (let i = 0; i < 5; i++) {
      const b = this.readByte();

      result |= (b & 0x7f) << shift;
      shift += 7;

      // Is there more?
      if ((b & 0x80) == 0) return result;
    }

    throw new Error('Malformed varint');
  }

  private decodeVarFloat() {
    const b = this.readByte();

    if (b == CanvasDecoder.VARFLOAT_ZERO) return 0;
    if (b == CanvasDecoder.VARFLOAT_ONE) return 1;
    if (b == CanvasDecoder.VARFLOAT_MINUS_ONE) return -1;

    if (b == CanvasDecoder.VARFLOAT_SHORT_FLOAT) {
      const floatBits = (this.readByte() << 24) | (this.readByte() << 16);
      return this.intBitsToFloat(floatBits);
    }

    const floatBits =
      (b << 24) | (this.readByte() << 16) | (this.readByte() << 8) | this.readByte();
    return this.intBitsToFloat(floatBits);
  }

  private decodeVarSInt() {
    return this.decodeZigZag32(this.decodeVarInt());
  }

  private readByte() {
    if (this.position >= this.limit) throw new Error('Read past end of buffer');

    return this.buffer.get(this.position++) & 0xff;
  }

  // https://github.com/google/protobuf/blob/master/java/src/main/java/com/google/protobuf/CodedInputStream.java
  private decodeZigZag32(n) {
    return (n >>> 1) ^ -(n & 1);
  }

  private intBitsToFloat(floatBits: number): number {
    const buf = new ArrayBuffer(4);
    new Uint32Array(buf)[0] = floatBits;
    return new Float32Array(buf)[0];
  }
}
